import React, { useEffect, useId } from 'react';
import ReactDOM from 'react-dom';

import Backdrop from '../backdrop/Backdrop';
import Icon from '../icon/Icon';
import './ModalPortal.scss';

interface IModalPortal {
  children: React.ReactNode;
  onClose?: () => void;
  className?: string;
  closeButtonOutside?: boolean;
  customStyles?: { [key: string]: string };
  modalContentClassName?: string;
  modalTitleClassName?: string;
  closeButtonClassName?: string;
  title?: string;
  onBack?: () => void;
  titleClassName?: string;
  showBackdrop?: boolean;
  backdropOpacity?: number;
}

const ModalPortal = ({
  title = '',
  className,
  modalContentClassName,
  modalTitleClassName = '',
  closeButtonClassName = '',
  onClose,
  onBack,
  children,
  customStyles = {},
  closeButtonOutside = false,
  titleClassName = '',
  showBackdrop = true,
  backdropOpacity = 0.75,
}: IModalPortal): React.ReactPortal => {
  const dialogId = useId();
  const closeButtonClasses = [
    closeButtonClassName,
    closeButtonOutside ? 'modal__title-close-button--outside' : '',
    'modal__title-close-button',
    'expand-hoover',
    'button--styleless',
  ];
  const modalTitleClassNames = [modalTitleClassName, 'modal__title'];

  useEffect(() => {
    document.addEventListener('keydown', onEscape, false);

    return () => {
      document.removeEventListener('keydown', onEscape, false);
    };
  }, []);

  const onEscape = (event: KeyboardEvent) => {
    if (onClose && event.key === 'Escape') {
      onClose();
    }
  };

  const hasModalTitleWrapper = onBack || title || onClose;

  return ReactDOM.createPortal(
    <div className={`modal ${className ? className : ''}`} role="dialog" aria-labelledby={`dialog-title-${dialogId}`}>
      {showBackdrop && (
        <div className="modal__backdrop" onClick={onClose}>
          <Backdrop show={true} opacity={backdropOpacity} />
        </div>
      )}
      <div className={`modal__content ${modalContentClassName || ''}`} style={customStyles}>
        {hasModalTitleWrapper && (
          <div className={modalTitleClassNames.join(' ')}>
            {onBack && (
              <button className="modal__title-back-button expand-hoover button--styleless" onClick={onBack}>
                <Icon className="modal__title-icon" id="back-icon" altText="Back to previous view" />
              </button>
            )}
            {title && (
              <h2 id={`dialog-title-${dialogId}`} className={titleClassName}>
                {title}
              </h2>
            )}
            {onClose && (
              <button className={closeButtonClasses.join(' ')} onClick={onClose}>
                <Icon className="modal__title-icon" id="close-icon" altText="Close modal" />
              </button>
            )}
          </div>
        )}
        {children}
      </div>
    </div>,
    document.querySelector('.modal-container') as Element
  );
};
export default ModalPortal;
